import React, { useEffect, useState } from "react";
import { Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import SEO from "../../components/site/SEO";
import { api, formatApiErrorDetail } from "../../lib/apiClient";
import { T } from "../../lib/testIds";

export default function Quote() {
  const [services, setServices] = useState([]);
  const [selected, setSelected] = useState([]);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", address: "", zip: "", notes: "", property_type: "residential", stories: 1, honeypot: "" });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => { api.get("/services").then(r => setServices(r.data)); }, []);

  const toggle = (id) => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/quotes", { ...form, services: selected, stories: Number(form.stories) });
      toast.success("Estimate request sent! We'll be in touch within the hour.");
      setSuccess(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to send");
    } finally { setSubmitting(false); }
  };

  if (success) {
    return (
      <section className="max-w-2xl mx-auto py-24 px-6" data-testid={T.quote.successCard}>
        <div className="ww-card-lg p-10 text-center">
          <div className="mx-auto h-14 w-14 rounded-full bg-[color:var(--ww-red)] text-white flex items-center justify-center mb-6"><Check className="h-7 w-7" /></div>
          <h1 className="font-display uppercase text-4xl text-[color:var(--ww-navy)]">Estimate request received</h1>
          <p className="mt-3 text-slate-600">A real Wonder Washers team member will follow up within the hour with pricing and next steps — including whether we'll do an on-site or photo-based estimate.</p>
        </div>
      </section>
    );
  }

  return (
    <>
      <SEO title="Free Estimate — Wonder Washers Pressure Washing" description="Request a fast, no-obligation estimate for house washing, driveways, decks, roofs, and commercial buildings." canonical="[BOOKING_URL]/quote" />
      <section className="bg-[color:var(--ww-navy)] text-white py-16 md:py-20 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          <div className="text-xs font-subhead tracking-widest text-[color:var(--ww-gold)] uppercase mb-3">Free Estimate</div>
          <h1 className="font-display uppercase text-5xl md:text-6xl leading-none">Tell us about your property.</h1>
          <p className="mt-4 text-slate-300 max-w-xl">Faster than phone tag — we'll follow up within the hour with real numbers. On-site walk-through or photo-based estimate, your call.</p>
        </div>
      </section>
      <section className="max-w-3xl mx-auto py-12 md:py-16 px-4 md:px-6">
        <form onSubmit={submit} className="ww-card-lg p-6 md:p-10 space-y-6">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-subhead text-slate-600 uppercase tracking-wider">First name *</label>
              <input data-testid={T.quote.firstName} required autoComplete="given-name" className="ww-input mt-1" value={form.first_name} onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))} />
            </div>
            <div>
              <label className="text-xs font-subhead text-slate-600 uppercase tracking-wider">Last name *</label>
              <input data-testid={T.quote.lastName} required autoComplete="family-name" className="ww-input mt-1" value={form.last_name} onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))} />
            </div>
            <div>
              <label className="text-xs font-subhead text-slate-600 uppercase tracking-wider">Email *</label>
              <input data-testid={T.quote.email} required type="email" autoComplete="email" className="ww-input mt-1" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
            </div>
            <div>
              <label className="text-xs font-subhead text-slate-600 uppercase tracking-wider">Mobile number *</label>
              <input data-testid={T.quote.phone} required type="tel" inputMode="tel" autoComplete="tel" className="ww-input mt-1" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="(541) 555-0117" />
            </div>
            <div>
              <label className="text-xs font-subhead text-slate-600 uppercase tracking-wider">Property type</label>
              <select className="ww-input mt-1" value={form.property_type} onChange={e => setForm(f => ({ ...f, property_type: e.target.value }))}>
                <option value="residential">Residential</option>
                <option value="commercial">Commercial</option>
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs font-subhead text-slate-600 uppercase tracking-wider">Property address</label>
              <input data-testid={T.quote.address} className="ww-input mt-1" value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} />
            </div>
            <div>
              <label className="text-xs font-subhead text-slate-600 uppercase tracking-wider">ZIP</label>
              <input className="ww-input mt-1" value={form.zip} onChange={e => setForm(f => ({ ...f, zip: e.target.value }))} />
            </div>
            <div>
              <label className="text-xs font-subhead text-slate-600 uppercase tracking-wider">Approx. sq ft (optional)</label>
              <input className="ww-input mt-1" value={form.stories} onChange={e => setForm(f => ({ ...f, stories: e.target.value }))} placeholder="e.g. 2400" />
            </div>
          </div>
          <div>
            <div className="text-xs font-subhead text-slate-600 uppercase tracking-wider mb-2">Services you're interested in</div>
            <div className="flex flex-wrap gap-2">
              {services.map(s => (
                <button
                  type="button"
                  key={s.id}
                  onClick={() => toggle(s.id)}
                  className={`px-4 py-2 rounded-md text-sm font-subhead uppercase tracking-wider border-2 transition-all ${selected.includes(s.id) ? "border-[color:var(--ww-red)] bg-red-50 text-[color:var(--ww-navy)]" : "border-slate-200 text-slate-700 hover:border-[color:var(--ww-pressure-blue)]"}`}
                >
                  {s.name}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-subhead text-slate-600 uppercase tracking-wider">Details / notes</label>
            <textarea data-testid={T.quote.notes} className="ww-input mt-1 min-h-[110px]" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} placeholder="What needs cleaning? Any access notes, timeline, or challenging surfaces?" />
          </div>
          <input tabIndex={-1} className="absolute -left-[9999px] w-0 h-0 opacity-0" value={form.honeypot} onChange={e => setForm(f => ({ ...f, honeypot: e.target.value }))} />
          <div className="text-xs text-slate-500">We'll never sell your info. By submitting you agree to be contacted about your estimate.</div>
          <button data-testid={T.quote.submit} type="submit" disabled={submitting} className="ww-btn-primary inline-flex items-center gap-2">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Request my estimate
          </button>
        </form>
      </section>
    </>
  );
}
